import { format, formatDistanceToNowStrict } from 'date-fns';
import { ko } from 'date-fns/locale';
import React, { useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Viewer from 'react-viewer';
import { ImageDecorator } from 'react-viewer/lib/ViewerProps';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Constants } from 'src/constants';
import { useDialog } from 'src/container/DialogContext';
import { ActivitySession, StudentActivitySession } from 'src/generated/model';
import { meState, toastState } from 'src/store';
import { getSessionTimeInfo } from 'src/util/activityv3';
import { getFileNameFromUrl, isPdfFile } from 'src/util/file';
import { Divider } from '../common';
import { ButtonV2 } from '../common/ButtonV2';
import { Typography } from '../common/Typography';
import { ErrorBlank } from '../ErrorBlank';
import SVGIcon from '../icon/SVGIcon';
import { SuperSurveyViewComponent } from '../survey/SuperSurveyViewComponent';
import { ActivityComments } from './ActivityComments';

interface StudentActivitySessionDetailViewProps {
  activitySession: ActivitySession;
  studentActivitySession?: StudentActivitySession;
  changeSubmitView?: () => void;
  isLoading?: boolean;
  refetch?: () => void;
}

export const StudentActivitySessionDetailView: React.FC<StudentActivitySessionDetailViewProps> = ({
  activitySession: activity,
  studentActivitySession: studentActivity,
  changeSubmitView,
  isLoading = false,
  refetch = () => {},
}) => {
  const me = useRecoilValue(meState);
  const { confirm } = useDialog();
  const images = studentActivity?.images || [];
  const files = studentActivity?.files || [];
  const [, setToastMsg] = useRecoilState(toastState);

  const [hasImagesModalOpen, setImagesModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const viewerImages: ImageDecorator[] = [];
  for (const image of images) {
    if (isPdfFile(image) == false) {
      viewerImages.push({
        src: `${Constants.imageUrl}${image}`,
      });
    }
  }

  let surveyContent = {};
  try {
    surveyContent = JSON.parse(studentActivity?.content || '{}');
  } catch (err) {}

  const { editableStatusText, isEditable, isLateSubmissionAvailable } = getSessionTimeInfo(activity);
  const checkEditStatus = async () => {
    if (!isEditable) {
      setToastMsg(editableStatusText);
      return false;
    } else if (isLateSubmissionAvailable) {
      if (
        !(await confirm(
          '지각제출 기간 알림',
          `현재 지각제출 기간입니다. 수정 시 '지각제출'로 표시됩니다.
학교 정책에 따라 감점이 될 수 있습니다.`,
          '취소',
          '확인',
        ))
      ) {
        return;
      }
    }
    return changeSubmitView?.();
  };
  if (!me) return <ErrorBlank />;
  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-col gap-6 px-5">
        {activity.type === 'SURVEY' && (
          <SuperSurveyViewComponent
            surveyContent={activity?.surveyContent || '[]'}
            setContent={() => {}}
            content={surveyContent}
            readOnly
          />
        )}
        {activity.type === 'POST' && <Typography variant="body1">{studentActivity?.content}</Typography>}
        {images && images.length > 0 && (
          <div className="flex w-full overflow-x-auto">
            <div className="flex flex-row gap-2">
              {images.map((image: string, i: number) => (
                <div
                  key={i}
                  className="h-20 w-20 flex-shrink-0 cursor-pointer rounded-lg"
                  onClick={() => {
                    setActiveIndex(i);
                    setImagesModalOpen(true);
                  }}
                >
                  <LazyLoadImage
                    src={`${Constants.imageUrl}${image}`}
                    alt=""
                    loading="lazy"
                    className="h-full w-full rounded-lg object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {files && files.length > 0 && (
          <div className="flex w-full flex-wrap gap-2">
            {files.map((fileUrl: string, index) => (
              <a
                href={`${Constants.imageUrl}${fileUrl}`}
                target="_blank"
                rel="noreferrer"
                download={getFileNameFromUrl(fileUrl)}
                key={index}
                className="flex h-10 w-max items-center gap-2 rounded-lg border border-primary-gray-200 bg-white px-3 py-2"
              >
                <SVGIcon.Link size={16} weight="bold" color="gray700" />
                <Typography variant="body2" className="line-clamp-1 text-primary-gray-700">
                  {getFileNameFromUrl(fileUrl)}
                </Typography>
              </a>
            ))}
          </div>
        )}

        <div className="absolute">
          <Viewer
            visible={hasImagesModalOpen}
            rotatable
            noImgDetails
            scalable={false}
            images={viewerImages}
            onChange={(activeImage, index) => setActiveIndex(index)}
            onClose={() => setImagesModalOpen(false)}
            activeIndex={activeIndex}
          />
        </div>
        {studentActivity?.isSubmitted && (
          <div className="flex flex-row items-center justify-between gap-3">
            <article className="flex flex-col gap-0.5">
              <Typography variant="caption2" className="flex flex-row items-center gap-1 text-primary-gray-500">
                <SVGIcon.Bullet color="gray400" size={16} />
                최초 제출일 : {format(new Date(studentActivity.createdAt), 'yyyy.MM.dd HH:mm')}&nbsp;(
                {formatDistanceToNowStrict(new Date(studentActivity.createdAt), {
                  locale: ko,
                  addSuffix: true,
                  unit: 'day',
                })}
                )
              </Typography>
              <Typography variant="caption2" className="flex flex-row items-center gap-1 text-primary-gray-500">
                <SVGIcon.Bullet color="gray400" size={16} />
                마지막 수정일 : {format(new Date(studentActivity.updatedAt), 'yyyy.MM.dd HH:mm')}&nbsp;(
                {formatDistanceToNowStrict(new Date(studentActivity.updatedAt), {
                  locale: ko,
                  addSuffix: true,
                  unit: 'day',
                })}
                )
              </Typography>
            </article>
            <ButtonV2
              variant="solid"
              color="gray100"
              size={40}
              children="수정"
              onClick={() => {
                checkEditStatus();
              }}
            />
          </div>
        )}
      </section>

      {/* 1:1 피드백 영역 */}
      <section className="flex flex-col gap-10 py-4">
        <Divider className="h-2.5 bg-primary-gray-100" />
        <div className="flex flex-col gap-4 px-5">
          <span className="flex flex-col gap-2">
            <Typography variant="title3" className="font-semibold">
              댓글
            </Typography>
            <ActivityComments
              id={studentActivity?.id || 0}
              comments={studentActivity?.sessionComments || []}
              user={me}
              refetch={() => refetch()}
            />
          </span>
        </div>
      </section>
    </div>
  );
};
